import React, { memo, useState, useEffect } from "react";
import isEmpty from "lodash/isEmpty";

import { Button, TextField, DialogActions, Typography } from "@mui/material";
import { Dialog } from "orion-components/CBComponents";

import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { compositeDeviceService } from "client-app-core";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";

const propTypes = {
	compositeDevice: PropTypes.object.isRequired,
	close: PropTypes.func,
	dir: PropTypes.string
};

const CompositeDeviceEditDialog = ({ compositeDevice, close, dir }) => {
	const dialog = useSelector((state) => state.appState?.dialog?.openDialog || "");
	const deviceId = compositeDevice ? compositeDevice.id : "";
	const dispatch = useDispatch();

	const [name, setName] = useState(compositeDevice ? compositeDevice.name : "");
	const [error, setError] = useState({
		name: ""
	});

	useEffect(() => {
		setName(compositeDevice?.name);
	}, [compositeDevice, dialog]);

	const handleClose = () => {
		close();
	};

	const handleNameChange = (event) => {
		if (event.target.value.length > 50) {
			setError({
				...error,
				name: getTranslation("global.profiles.deviceProfile.Dialog.errorText.lessThanFifty")
			});
			return;
		}
		setName(event.target.value);
		setError({ ...error, name: "" });
	};

	const handleCancel = () => {
		setName("");
		handleClose();
	};

	const handleSave = () => {
		if (!name) {
			setError({
				...error,
				name: getTranslation("global.profiles.deviceProfile.Dialog.errorText.noName")
			});
			return;
		}

		compositeDeviceService.updateCompositeDeviceId(compositeDevice?.id, { ...compositeDevice, name: name }, (err) => {
			if (err) {
				console.error(err, "error updating composite device");
				dispatch(
					createUserFeedback(
						getTranslation(
							"global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.compositeDeviceUpdateFailed"
						),
						null,
						true
					)
				);
			}
		});

		handleClose();
	};

	const dialogStyles = {
		dialog: {
			maxWidth: 500
		}
	};

	const textFieldStyles = {
		marginBottom: "2rem",
		backgroundColor: "#2C2D2F", // Force styling in Map app
		input: { textOverflow: "ellipsis" }
	};

	const actions = [
		<Button style={{ color: "#B2B6BB" }} variant="text" onClick={handleCancel} key="cancel-action-button">
			<Translate value="global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.cancel" />
		</Button>,
		<Button
			color="primary"
			variant="text"
			onClick={handleSave}
			key="confirm-action-button"
			disabled={isEmpty(name) || name === compositeDevice?.name}
		>
			<Translate value="global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.confirm" />
		</Button>
	];

	const dialogTitle = [
		<Typography sx={{ fontWeight: "400", fontSize: "22px" }} key="dialog-title">
			<Translate value="global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.title" />
		</Typography>
	];

	const dialogActions = () => {
		return <DialogActions>{actions}</DialogActions>;
	};

	return (
		<Dialog
			open={dialog === "compositeDeviceEditDialog"}
			paperPropStyles={dialogStyles.dialog}
			dialogContentStyles={{
				paddingTop: "25px",
				paddingBottom: "25px",
				borderTop: "none",
				borderBottom: "none"
			}}
			titlePropStyles={{ marginBottom: "1.5rem" }}
			title={dialogTitle}
			actions={true}
			DialogActionsFunction={dialogActions}
		>
			<div>
				<TextField
					id="compositeDevice-name"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.compositeDeviceName")}
					value={name}
					onChange={handleNameChange}
					sx={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					errorText={error.name}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
					autoFocus={true}
				/>
				<TextField
					id="compositeDevice-id"
					fullWidth={true}
					variant="standard"
					label={getTranslation("global.profiles.compositeDeviceProfile.components.compositeDeviceEditDialog.compositeDeviceId")}
					disabled={true}
					value={deviceId}
					style={textFieldStyles}
					InputProps={{ style: textFieldStyles.input }}
					InputLabelProps={{
						style: {
							color: "#646465",
							transformOrigin: dir && dir == "rtl" ? "top right" : "top left",
							left: "unset"
						}
					}}
				/>
			</div>
		</Dialog>
	);
};

const componentUpdate = (prevProps, nextProps) => {
	if (prevProps.compositeDevice !== nextProps.compositeDevice) {
		return false;
	} else {
		return true;
	}
};

CompositeDeviceEditDialog.propTypes = propTypes;

export default memo(CompositeDeviceEditDialog, componentUpdate);
